<script setup lang="ts">
import { vElementVisibility } from '@vueuse/components'
import { NewOpportunitiesAlertModal } from '#components'

const config = useRuntimeConfig()
const platform = useState('platform')

const props = defineProps({
  search: Object|null,
  infiniteScroll: {
    type: Boolean,
    default: false
  },
  newOpportunitiesAlert: {
    type: Boolean,
    default: false
  },
  widget: { type: [Boolean, String], default: false },
  location: { type: String, default: null },
  external: { type: Boolean, default: false },
})

const missions = ref([]);
const nextCursor = ref(null);

const activity = computed(() => props.search?.activity)
const domain = computed(() => props.search?.domain)
const coordinates = computed(() => props.search?.coordinates)
const filter = computed(() => props.search?.filter)
const remote = computed(() => props.search?.remote)
const group = computed(() => props.search?.group)
const organization = computed(() => props.search?.organization)
const template = computed(() => props.search?.template)
const search = computed(() => props.search?.q)
const utmSource = computed(() => props.search?.utm_source)
const utmCampaign = computed(() => props.search?.utm_campaign)
const exclude = computed(() => props.search?.exclude)
const perPage = computed(() => props.search?.['per-page'] && !nextCursor.value ? props.search?.['per-page'] : 12)

function onMissionCardShow(mission: object) {
  return (state: boolean) => {
    if (state) {
      useActivity({
        name: 'show',
        subject_type: 'mission-session',
        subject_id: mission.session_id ? mission.session_id : mission.id,
        location: props.widget ? 'widget' : props.location
      })
    }
  }
}

const {execute, refresh, status} = useFetch(`https://api.${useBenevoltDomain()}/v1/search/mission`, {
  query: {
    activity,
    domain,
    coordinates,
    filter,
    remote,
    'organization-group': group,
    organization,
    template,
    search,
    'utm_source': utmSource,
    'utm_campaign': utmCampaign,
    exclude,
    'per-page': perPage,
    api_key: props.widget.api_key ?? platform.value.api_key,
    cursor: nextCursor,
  },
  key: () => 'mission-search-' + JSON.stringify(props.search), // TODO: https://github.com/nuxt/nuxt/issues/32437
  watch: false,
  lazy: true,
  immediate: false,
  server: false,
  transform: (res) => {
    missions.value = missions.value.concat(res.data.map((m) => {
      const url = new URL(m.url);
      return {...m, url: url.pathname + url.search}
    }));
    nextCursor.value = res.next_cursor;
    return res;
  },
});

onMounted(async () => {
  await execute()
})

watch(() => JSON.stringify(props.search), async () => {
  nextCursor.value = null;
  missions.value = []
  await execute();
});

const canLoadMore = computed(() => status.value === 'success' && !!nextCursor.value)

const rootEl = ref(null)
const isVisible = useElementVisibility(rootEl)

useInfiniteScroll(
    document,
    async () => {
      await refresh();
    },
    {
      canLoadMore: () => isVisible.value && canLoadMore.value && props.infiniteScroll,
      distance: 700,
    }
);

const count = computed(() => missions?.value?.length || 0)
defineExpose({
  count,
  status,
})

const overlay = useOverlay()
const modal = overlay.create(NewOpportunitiesAlertModal)
</script>

<template>
  <div ref="rootEl">
    <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <MissionSearchCard :mission="mission" v-for="mission in missions" :key="mission.id" :external="external" :target="widget ? '_blank' : '_self'" v-element-visibility="onMissionCardShow(mission)" />
      <UPageCard :ui="{root: 'h-80 hover:ring-gray-300 hover:dark:ring-gray-700 hover:shadow-lg bg-gray-100'}" v-if="!nextCursor && missions.length && newOpportunitiesAlert">
        <div class="flex flex-col items-center justify-center h-full gap-4">
          <p class="text-2xl font-bold text-center">Devenir bénévole<br>sur d'autres missions ?</p>
          <p class="text-center">Vous serez informé dès<br>qu'une mission sera publiée</p>
          <UButton icon="i-ph-bell-duotone" @click="widget ? navigateTo({path: '/recherche', query: {...useRoute().query, alert: true, utm_source: utmSource, utm_campaign: utmCampaign}}, {open: {target: '_blank'}}) : modal.open()">Rester informé</UButton>
        </div>
      </UPageCard>
      <MissionSearchCard :mission="null" v-for="i in perPage" :key="`empty-mission-${i}`" v-if="status == 'pending' || status == 'idle'" />
    </div>
  </div>

  <div class="flex justify-center mt-10" v-if="!infiniteScroll">
    <UButton size="xl" class="px-8 rounded-full font-semibold" v-if="canLoadMore" @click="execute()">Voir plus de résultats</UButton>
  </div>
</template>